<!-- 保险管理 -->
<template>
  <v-container fluid>
    <!-- 查询条件：车牌号、保险公司、险种、到期状态 -->
    <v-row class="query-row">
      <v-col cols="12" xl="2" lg="2" md="4" sm="6">
        <v-text-field v-model="queryForm.vehicle_number" label="车牌号" dense outlined clearable hide-details
          @keyup.enter="search()"></v-text-field>
      </v-col>
      <v-col cols="12" xl="2" lg="2" md="4" sm="6">
        <v-text-field v-model="queryForm.insurance_company" label="保险公司" dense outlined clearable hide-details
          @keyup.enter="search()"></v-text-field>
      </v-col>
      <v-col cols="12" xl="2" lg="2" md="4" sm="6">
        <v-select v-model="queryForm.insurance_type" :items="insuranceTypeOptions" item-text="label" item-value="value"
          label="险种" no-data-text="无" dense outlined clearable hide-details
          :menu-props="{ bottom: true, offsetY: true }">
        </v-select>
      </v-col>
      <v-col cols="12" xl="2" lg="2" md="4" sm="6">
        <v-select v-model="queryForm.expire_status" :items="expireStatusOptions" item-text="label" item-value="value"
          label="到期状态" no-data-text="无" dense outlined clearable hide-details
          :menu-props="{ bottom: true, offsetY: true }">
        </v-select>
      </v-col>
      <v-col cols="12" xl="3" lg="3" md="6" sm="6">
        <a-space>
          <v-btn @click="search()" color="primary" dark tile>
            <v-icon small>mdi-magnify</v-icon>查询
          </v-btn>
          <v-btn @click="openCreate" color="success" dark tile>
            <v-icon small>mdi-plus</v-icon>新增保险
          </v-btn>
        </a-space>
      </v-col>
    </v-row>

    <v-card tile>
      <v-card-title class="d-flex align-center py-2 px-3" style="background-color: #fff;">
        <span class="text-subtitle-2">共 {{ count }} 条数据</span>
        <v-spacer></v-spacer>
        <v-menu bottom offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn small outlined v-bind="attrs" v-on="on" class="grey lighten-3">{{ limit }}/页</v-btn>
          </template>
          <v-list>
            <v-list-item v-for="item in pages" :key="item.value" @click="changeLimit(item.value)">
              <v-list-item-title>{{ item.value }}/页</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-card-title>
      <a-table size="small" :columns="headers" :data-source="pageList" row-key="_id" :pagination="false" bordered
        :locale="{ emptyText: '暂无数据' }" :scroll="{ x: width }">
        <!-- 序号：按分页连续编号 -->
        <template slot="index" slot-scope="text,record,index">
          {{ (page - 1) * limit + index + 1 }}
        </template>
        <!-- 车架号等宽小字体，避免过长挤压 -->
        <template slot="vin" slot-scope="text">
          <span class="text-body-2">{{ text }}</span>
        </template>
        <!-- 险种标签 -->
        <template slot="insurance_type" slot-scope="text">
          <a-tag :color="insuranceTypeColor(text)">{{ text }}</a-tag>
        </template>
        <!-- 保费保留两位小数 -->
        <template slot="premium" slot-scope="text">
          <span class="font-weight-bold">{{ formatMoney(text) }}</span>
        </template>
        <!-- 剩余天数：自动计算 = 到期日期 - 今天；<=30天橙色，已过期红色 -->
        <template slot="days_left" slot-scope="text">
          <a-tag v-if="text < 0" color="red">已过期 {{ -text }} 天</a-tag>
          <a-tag v-else-if="text <= 30" color="orange">{{ text }} 天</a-tag>
          <span v-else>{{ text }} 天</span>
        </template>
        <!-- 备注超长省略 -->
        <template slot="remark" slot-scope="text">
          <a-tooltip v-if="text" :title="text" placement="topLeft">
            <span>{{ text }}</span>
          </a-tooltip>
        </template>
        <!-- 操作：编辑 / 删除（nowrap 防止挤压换行；列少不固定列） -->
        <template slot="action" slot-scope="text, record">
          <a-space :size="4" style="white-space: nowrap;">
            <a @click="openEdit(record)">编辑</a>
            <a-divider type="vertical" />
            <a-popconfirm :title="`确认删除「${record.vehicle_number} - ${record.insurance_type}」保单?`" ok-text="确定"
              cancel-text="取消" placement="left" @confirm="removeItem(record)">
              <a class="error--text">删除</a>
            </a-popconfirm>
          </a-space>
        </template>
      </a-table>
    </v-card>

    <v-card tile class="mt-2">
      <v-pagination v-if="length > 1" v-model="page" :page="page" :length="length" total-visible="10"></v-pagination>
    </v-card>

    <!-- 新增 / 编辑保险弹窗 -->
    <v-dialog v-model="dialogVisible" max-width="900" persistent scrollable>
      <v-card>
        <v-card-title class="primary white--text">
          {{ dialogMode === "create" ? "新增保险" : "编辑保险" }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-form ref="form" v-model="formValid" :lazy-validation="true">
            <v-row>
              <v-col cols="12" md="4" sm="6">
                <v-text-field v-model="form.vehicle_number" label="车牌号" dense outlined required
                  :rules="[v => !!v || '请填写车牌号']"></v-text-field>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <!-- 车辆类型：常用项可选，也允许手动输入 -->
                <v-combobox v-model="form.vehicle_type" :items="vehicleTypeOptions" label="车辆类型" dense outlined
                  clearable hide-details :rules="[v => !!v || '请选择/填写车辆类型']"></v-combobox>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <v-combobox v-model="form.tonnage" :items="tonnageOptions" label="车型吨位" dense outlined clearable
                  hide-details :rules="[v => !!v || '请选择/填写车型吨位']"></v-combobox>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <!-- 车架号(VIN)通常17位，使用文本避免精度丢失 -->
                <v-text-field v-model="form.vin" label="车架号" dense outlined maxlength="17"></v-text-field>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <v-combobox v-model="form.insurance_company" :items="companyOptions" label="保险公司" dense outlined
                  clearable hide-details :rules="[v => !!v || '请选择/填写保险公司']"></v-combobox>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <v-select v-model="form.insurance_type" :items="insuranceTypeOptions" item-text="label"
                  item-value="value" label="险种" dense outlined clearable hide-details required
                  :rules="[v => !!v || '请选择险种']" :menu-props="{ bottom: true, offsetY: true }"></v-select>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <v-text-field v-model="form.policy_holder" label="投保人" dense outlined></v-text-field>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <v-text-field v-model="form.insured" label="被保险人" dense outlined></v-text-field>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <v-text-field v-model="form.salesman" label="业务员" dense outlined></v-text-field>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <v-menu ref="issue_menu" v-model="issue_menu" :close-on-content-click="false"
                  :return-value.sync="form.issue_date" transition="scale-transition" min-width="auto" offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="form.issue_date" label="出单日期" append-icon="mdi-calendar" readonly
                      v-bind="attrs" v-on="on" dense outlined clearable required hide-details
                      :rules="[v => !!v || '请选择出单日期']"></v-text-field>
                  </template>
                  <v-date-picker v-model="form.issue_date" no-title scrollable locale="zh-cn"
                    @input="() => { $refs.issue_menu.save(form.issue_date); issue_menu = false; }"></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <v-menu ref="start_menu" v-model="start_menu" :close-on-content-click="false"
                  :return-value.sync="form.start_date" transition="scale-transition" min-width="auto" offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="form.start_date" label="生效日期" append-icon="mdi-calendar" readonly
                      v-bind="attrs" v-on="on" dense outlined clearable required hide-details
                      :rules="[v => !!v || '请选择生效日期']"></v-text-field>
                  </template>
                  <v-date-picker v-model="form.start_date" no-title scrollable locale="zh-cn"
                    @input="() => { $refs.start_menu.save(form.start_date); start_menu = false; }"></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <v-menu ref="end_menu" v-model="end_menu" :close-on-content-click="false"
                  :return-value.sync="form.end_date" transition="scale-transition" min-width="auto" offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="form.end_date" label="到期日期" append-icon="mdi-calendar" readonly
                      v-bind="attrs" v-on="on" dense outlined clearable required hide-details
                      :rules="[v => !!v || '请选择到期日期']"></v-text-field>
                  </template>
                  <v-date-picker v-model="form.end_date" no-title scrollable locale="zh-cn"
                    @input="() => { $refs.end_menu.save(form.end_date); end_menu = false; }"></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <v-text-field v-model.number="form.premium" label="保费(元)" type="number" dense outlined required
                  :rules="[v => (v !== '' && v != null && Number(v) > 0) || '请输入大于0的保费']"></v-text-field>
              </v-col>
              <v-col cols="12" md="4" sm="6">
                <!-- 剩余天数自动计算，只读展示 -->
                <v-text-field :value="form.end_date ? daysLeft(form.end_date) + ' 天' : ''" label="剩余天数(自动计算)"
                  dense outlined readonly hide-details></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea v-model="form.remark" label="备注" dense outlined rows="2"></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text tile @click="dialogVisible = false">取消</v-btn>
          <v-btn color="primary" dark tile @click="submitForm">保存</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import _ from "underscore";

export default {
  name: "Insurance",
  data() {
    return {
      // mock 开关：true=本地模拟数据；后端接口出来后改为 false，并打开 getdata/save/remove 里的真实请求
      useMock: true,
      // 查询条件：车牌号、保险公司、险种、到期状态
      queryForm: {
        vehicle_number: "",
        insurance_company: "",
        insurance_type: null,
        expire_status: null,
      },
      // 险种选项
      insuranceTypeOptions: [
        { label: "交强险", value: "交强险" },
        { label: "商业险", value: "商业险" },
        { label: "交强险+商业险", value: "交强险+商业险" },
        { label: "承运人责任险", value: "承运人责任险" },
        { label: "驾乘人员意外险", value: "驾乘人员意外险" },
      ],
      // 到期状态：normal 有效 / warning 30天内到期 / expired 已过期
      expireStatusOptions: [
        { label: "有效", value: "normal" },
        { label: "30天内到期", value: "warning" },
        { label: "已过期", value: "expired" },
      ],
      // 车辆类型/吨位/保险公司常用项（combobox 允许手动输入补充）
      vehicleTypeOptions: ["半挂牵引车", "重型载货车", "中型载货车", "轻型载货车", "仓栅式运输车"],
      tonnageOptions: ["12吨", "18吨", "30吨", "31吨", "42吨", "49吨"],
      companyOptions: ["中国人民财产保险", "中国平安财产保险", "中国太平洋财产保险", "中国人寿财产保险", "中华联合财产保险"],
      pages: [
        { text: "20/页", value: 20 },
        { text: "50/页", value: 50 },
        { text: "100/页", value: 100 },
      ],
      headers: [
        { title: "序号", dataIndex: "index", scopedSlots: { customRender: "index" }, width: 60, align: "center" },
        { title: "车牌号", dataIndex: "vehicle_number", width: 110, align: "center" },
        { title: "车辆类型", dataIndex: "vehicle_type", width: 120, align: "center" },
        { title: "车型吨位", dataIndex: "tonnage", width: 90, align: "center" },
        { title: "车架号", dataIndex: "vin", scopedSlots: { customRender: "vin" }, width: 170, align: "center" },
        { title: "保险公司", dataIndex: "insurance_company", width: 150, align: "left", ellipsis: true },
        { title: "投保人", dataIndex: "policy_holder", width: 120, align: "center" },
        { title: "被保险人", dataIndex: "insured", width: 140, align: "center" },
        { title: "业务员", dataIndex: "salesman", width: 90, align: "center" },
        { title: "出单日期", dataIndex: "issue_date", width: 110, align: "center" },
        { title: "险种", dataIndex: "insurance_type", scopedSlots: { customRender: "insurance_type" }, width: 140, align: "center" },
        { title: "保费(元)", dataIndex: "premium", scopedSlots: { customRender: "premium" }, width: 100, align: "center" },
        { title: "生效日期", dataIndex: "start_date", width: 110, align: "center" },
        { title: "到期日期", dataIndex: "end_date", width: 110, align: "center" },
        { title: "剩余天数", dataIndex: "days_left", scopedSlots: { customRender: "days_left" }, width: 120, align: "center" },
        { title: "备注", dataIndex: "remark", scopedSlots: { customRender: "remark" }, width: 160, align: "left", ellipsis: true },
        { title: "操作", dataIndex: "action", scopedSlots: { customRender: "action" }, width: 120, align: "center" },
      ],
      // 全量 mock 数据
      allList: [],
      // 查询过滤后的数据
      list: [],
      page: 1,
      limit: 20,
      count: 0,
      length: 0,
      // 新增/编辑弹窗
      dialogVisible: false,
      dialogMode: "create", // create / edit
      formValid: false,
      form: this.emptyForm(),
      issue_menu: false,
      start_menu: false,
      end_menu: false,
    };
  },
  computed: {
    width() {
      return _.reduce(this.headers, (a, b) => a + (b.width || 120), 0);
    },
    // 当前页数据（前端分页）
    pageList() {
      const start = (this.page - 1) * this.limit;
      return this.list.slice(start, start + this.limit).map((it) => ({
        ...it,
        days_left: this.daysLeft(it.end_date), // 剩余天数由到期日期自动计算
      }));
    },
  },
  mounted() {
    this.getdata();
  },
  methods: {
    emptyForm() {
      return {
        _id: "",
        vehicle_number: "",
        vehicle_type: "",
        tonnage: "",
        vin: "",
        insurance_company: "",
        policy_holder: "",
        insured: "",
        salesman: "",
        issue_date: "",
        insurance_type: null,
        premium: "",
        start_date: "",
        end_date: "",
        remark: "",
      };
    },
    formatMoney(v) {
      return v == null || v === "" ? "0.00" : Number(v).toFixed(2);
    },
    // 剩余天数 = 到期日期 - 今天（按自然日，负数表示已过期）
    daysLeft(endDate) {
      if (!endDate) return "";
      const end = this.$moment(endDate).startOf("day");
      const today = this.$moment().startOf("day");
      return end.diff(today, "day");
    },
    insuranceTypeColor(type) {
      const map = {
        交强险: "blue",
        商业险: "cyan",
        "交强险+商业险": "geekblue",
        承运人责任险: "purple",
        驾乘人员意外险: "gold",
      };
      return map[type] || "default";
    },
    search() {
      this.page = 1;
      this.getdata();
    },
    getdata() {
      if (this.useMock) {
        this.getMockData();
        return;
      }
      // TODO 接口联调：POST /insurance/list
      // 参数：vehicle_number 车牌号；insurance_company 保险公司；insurance_type 险种；expire_status 到期状态
      // 剩余天数建议后端按当天日期直接返回 days_left；前端也可根据 end_date 自行计算
      // this.$toast.loading({ message: "" });
      // this.$api.doAPI("/insurance/list", { page: this.page, limit: this.limit, ...this.queryForm }).then((res) => {
      //   this.$toast.clear();
      //   if (res.code === 0) {
      //     this.list = res.data.list;
      //     this.count = res.data.count;
      //     this.length = res.data.length || Math.max(1, Math.ceil(this.count / this.limit));
      //   } else {
      //     this.$alert(res.msg);
      //   }
      // }).catch((err) => { this.$toast.clear(); console.error(err); });
    },
    // 模拟接口数据
    getMockData() {
      if (!this.allList.length) {
        const m = this.$moment;
        // 以今天为基准构造到期日期，覆盖有效/临期/过期场景
        const raw = [
          { vehicle_number: "豫A37230D", vehicle_type: "半挂牵引车", tonnage: "49吨", vin: "LFWSRXSJ9L1A00123", insurance_company: "中国人民财产保险", policy_holder: "河南立达物流有限公司", insured: "河南立达物流有限公司", salesman: "赵明辉", issue_date: m().subtract(300, "days").format("YYYY-MM-DD"), insurance_type: "交强险+商业险", premium: 12860, start_date: m().subtract(298, "days").format("YYYY-MM-DD"), end_date: m().add(67, "days").format("YYYY-MM-DD"), remark: "含三者险200万、车损险" },
          { vehicle_number: "豫A37301D", vehicle_type: "半挂牵引车", tonnage: "42吨", vin: "LFWSRXSJ7K2B00456", insurance_company: "中国平安财产保险", policy_holder: "河南立达物流有限公司", insured: "河南立达物流有限公司", salesman: "钱芳", issue_date: m().subtract(340, "days").format("YYYY-MM-DD"), insurance_type: "交强险", premium: 4480, start_date: m().subtract(338, "days").format("YYYY-MM-DD"), end_date: m().add(27, "days").format("YYYY-MM-DD"), remark: "即将到期，注意续保" },
          { vehicle_number: "豫A37150D", vehicle_type: "重型载货车", tonnage: "31吨", vin: "LGAX4C432L0C00789", insurance_company: "中国太平洋财产保险", policy_holder: "河南立达物流有限公司", insured: "河南立达物流有限公司", salesman: "孙涛", issue_date: m().subtract(400, "days").format("YYYY-MM-DD"), insurance_type: "商业险", premium: 8600, start_date: m().subtract(398, "days").format("YYYY-MM-DD"), end_date: m().subtract(33, "days").format("YYYY-MM-DD"), remark: "已过期，车辆暂停营运" },
          { vehicle_number: "豫A37668D", vehicle_type: "仓栅式运输车", tonnage: "18吨", vin: "LVBV7PEC8LJ00321", insurance_company: "中国人寿财产保险", policy_holder: "河南立达物流有限公司", insured: "河南立达物流有限公司", salesman: "周丽", issue_date: m().subtract(15, "days").format("YYYY-MM-DD"), insurance_type: "交强险+商业险", premium: 9750.5, start_date: m().subtract(13, "days").format("YYYY-MM-DD"), end_date: m().add(352, "days").format("YYYY-MM-DD"), remark: "" },
          { vehicle_number: "豫A38821D", vehicle_type: "半挂牵引车", tonnage: "49吨", vin: "LFWSRXRJXL1D00654", insurance_company: "中国人民财产保险", policy_holder: "河南立达物流有限公司", insured: "河南立达物流有限公司", salesman: "赵明辉", issue_date: m().subtract(120, "days").format("YYYY-MM-DD"), insurance_type: "承运人责任险", premium: 3600, start_date: m().subtract(118, "days").format("YYYY-MM-DD"), end_date: m().add(247, "days").format("YYYY-MM-DD"), remark: "每车每座保额50万" },
          { vehicle_number: "豫A39076D", vehicle_type: "中型载货车", tonnage: "12吨", vin: "LFNA4LBA9LAC00987", insurance_company: "中华联合财产保险", policy_holder: "河南立达物流有限公司", insured: "河南立达物流有限公司", salesman: "吴静", issue_date: m().subtract(355, "days").format("YYYY-MM-DD"), insurance_type: "驾乘人员意外险", premium: 1200, start_date: m().subtract(353, "days").format("YYYY-MM-DD"), end_date: m().add(12, "days").format("YYYY-MM-DD"), remark: "" },
        ];
        this.allList = raw.map((it, i) => ({
          _id: "ins_" + (i + 1),
          ...it,
        }));
      }
      // 条件过滤：车牌号 + 保险公司 + 险种 + 到期状态
      let result = [...this.allList];
      if (this.queryForm.vehicle_number) {
        const kw = this.queryForm.vehicle_number.trim();
        result = result.filter((it) => (it.vehicle_number || "").includes(kw));
      }
      if (this.queryForm.insurance_company) {
        const kw = this.queryForm.insurance_company.trim();
        result = result.filter((it) => (it.insurance_company || "").includes(kw));
      }
      if (this.queryForm.insurance_type) {
        result = result.filter((it) => it.insurance_type === this.queryForm.insurance_type);
      }
      if (this.queryForm.expire_status) {
        result = result.filter((it) => {
          const d = this.daysLeft(it.end_date);
          if (this.queryForm.expire_status === "expired") return d < 0;
          if (this.queryForm.expire_status === "warning") return d >= 0 && d <= 30;
          return d > 30; // normal
        });
      }
      this.list = result;
      this.count = result.length;
      this.length = Math.max(1, Math.ceil(this.count / this.limit));
      if (this.page > this.length) this.page = 1;
    },
    changeLimit(v) {
      this.limit = v;
      this.page = 1;
      this.getdata();
    },
    openCreate() {
      this.form = this.emptyForm();
      this.dialogMode = "create";
      this.dialogVisible = true;
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },
    openEdit(record) {
      this.form = { ...this.emptyForm(), ..._.pick(record, Object.keys(this.emptyForm())) };
      this.dialogMode = "edit";
      this.dialogVisible = true;
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },
    submitForm() {
      this.$refs.form.validate((valid) => {
        if (!valid) return;
        // 日期先后顺序校验
        if (this.form.start_date && this.form.end_date && this.form.end_date < this.form.start_date) {
          this.$message.error("到期日期不能早于生效日期");
          return;
        }
        if (this.useMock) {
          this.saveMock();
          return;
        }
        // TODO 接口联调：POST /insurance/save（新增/编辑，编辑传 _id）
        // this.$toast.loading({ message: "" });
        // this.$api.doAPI("/insurance/save", { ...this.form }).then((res) => {
        //   this.$toast.clear();
        //   if (res.code === 0) {
        //     this.$message.success(this.dialogMode === "create" ? "新增成功" : "已更新");
        //     this.dialogVisible = false;
        //     this.getdata();
        //   } else {
        //     this.$alert(res.msg);
        //   }
        // }).catch((err) => { this.$toast.clear(); console.error(err); });
      });
    },
    saveMock() {
      const data = {
        ...this.form,
        premium: Math.round(Number(this.form.premium) * 100) / 100,
      };
      if (this.dialogMode === "create") {
        this.allList = [{ ...data, _id: "ins_" + Date.now() }, ...this.allList];
        this.$message.success("新增成功");
      } else {
        this.allList = this.allList.map((it) => (it._id === data._id ? { ...it, ...data } : it));
        this.$message.success("已更新");
      }
      this.dialogVisible = false;
      this.getdata();
    },
    removeItem(record) {
      if (this.useMock) {
        this.allList = this.allList.filter((it) => it._id !== record._id);
        this.$message.success("已删除");
        this.getdata();
        return;
      }
      // TODO 接口联调：POST /insurance/remove
      // 参数：_id 保单ID
      // this.$toast.loading({ message: "" });
      // this.$api.doAPI("/insurance/remove", { _id: record._id }).then((res) => {
      //   this.$toast.clear();
      //   if (res.code === 0) {
      //     this.$message.success("已删除");
      //     this.getdata();
      //   } else {
      //     this.$alert(res.msg);
      //   }
      // }).catch((err) => { this.$toast.clear(); console.error(err); });
    },
  },
};
</script>
<style lang="less" scoped>
/deep/ .row {
  margin-bottom: 0 !important;
}

/* 查询条件行与下方表格保持间距 */
/deep/ .row.query-row {
  margin-bottom: 16px !important;
}

/deep/ .col,
.col-12 {
  padding-bottom: 0 !important;
}

/deep/ .v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}

p {
  margin: 0;
}
</style>
